import React from 'react'
import config from '../../config.js'
import { apiErrorMessage } from '../../utils/apiError';

const NESTER = config.NESTER_API_URL

/**
 * Exports tab — central place to download everything related to the project.
 */
export default function NesterExportsTab({
  colors,
  project,
  nestResult,
  onOpenCreateParts,
  onOpenCreateCutSheets,
  onOpenCreateQuote,
  canCreateQuote,
}) {
  const groups = nestResult?.group_results || (nestResult ? [nestResult] : [])

  async function download(url, filename) {
    try {
      const res = await fetch(url)
      if (!res.ok) throw new Error(await apiErrorMessage(res));
      const blob = await res.blob()
      const objectUrl = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = objectUrl
      a.download = filename
      document.body.appendChild(a)
      a.click()
      a.remove()
      URL.revokeObjectURL(objectUrl)
    } catch (e) {
      alert('Download failed: ' + (e.message || e))
    }
  }

  return (
    <div style={{ padding: 16 }}>
      <Section colors={colors} title="Per-group exports" subtitle="CSV and PDF for each nested material group.">
        {groups.length === 0 ? (
          <Empty colors={colors}>No nest generated yet.</Empty>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: 10 }}>
            {groups.map((g, i) => {
              const label = g.material_group_name || `Group ${i + 1}`
              return (
                <div key={g.nest_id || i} style={styles.card(colors)}>
                  <div style={{ fontWeight: 700, fontSize: 13, color: colors.text, marginBottom: 8 }}>
                    {label}
                  </div>
                  <div style={{ display: 'flex', gap: 6 }}>
                    <button onClick={() => download(`${NESTER}/nests/${g.nest_id}/csv`, `nest-${g.nest_id}.csv`)}
                      disabled={!g.nest_id}
                      style={styles.btn(colors)}>⇣ CSV</button>
                    <button onClick={() => download(`${NESTER}/nests/${g.nest_id}/pdf`, `nest-${g.nest_id}.pdf`)}
                      disabled={!g.nest_id}
                      style={styles.btn(colors)}>⇣ PDF</button>
                  </div>
                </div>
              )
            })}
          </div>
        )}
      </Section>

      <Section colors={colors} title="Send to JobQ" subtitle="Flow the nest into production or quoting.">
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
          <button onClick={onOpenCreateParts}
            disabled={!project}
            style={styles.bigBtn(colors)}>
            <div style={{ fontSize: 13, fontWeight: 700 }}>Create Parts</div>
            <div style={{ fontSize: 11, color: colors.textMuted, marginTop: 3 }}>
              Create production part entries in JobQ from these DXFs
            </div>
          </button>
          <button onClick={onOpenCreateCutSheets}
            disabled={!nestResult}
            style={styles.bigBtn(colors)}>
            <div style={{ fontSize: 13, fontWeight: 700 }}>Create Cut Sheets</div>
            <div style={{ fontSize: 11, color: colors.textMuted, marginTop: 3 }}>
              Seed production cut sheets from this nest layout
            </div>
          </button>
          <button onClick={onOpenCreateQuote}
            disabled={!canCreateQuote}
            style={styles.bigBtn(colors)}
            title={!canCreateQuote ? 'Assign a customer to the project first' : ''}>
            <div style={{ fontSize: 13, fontWeight: 700 }}>Create Quote</div>
            <div style={{ fontSize: 11, color: colors.textMuted, marginTop: 3 }}>
              Generate a customer-facing quote with part-level pricing
            </div>
          </button>
        </div>
      </Section>

      <Section colors={colors} title="Printable packets (coming soon)"
        subtitle="One-click PDFs for the shop floor and for customers.">
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
          <button disabled style={styles.bigBtn(colors)}>
            <div style={{ fontSize: 13, fontWeight: 700 }}>Shop-floor packet</div>
            <div style={{ fontSize: 11, color: colors.textMuted, marginTop: 3 }}>
              Per-sheet layout + pierce map + cut order + material spec
            </div>
          </button>
          <button disabled style={styles.bigBtn(colors)}>
            <div style={{ fontSize: 13, fontWeight: 700 }}>Professional quote PDF</div>
            <div style={{ fontSize: 11, color: colors.textMuted, marginTop: 3 }}>
              Branded quote with embedded nest thumbnails + per-part images
            </div>
          </button>
        </div>
      </Section>
    </div>
  )
}

function Section({ colors, title, subtitle, children }) {
  return (
    <div style={{ marginBottom: 22 }}>
      <div style={{ fontSize: 13, fontWeight: 700, color: colors.text, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
        {title}
      </div>
      {subtitle && (
        <div style={{ fontSize: 12, color: colors.textMuted, margin: '2px 0 10px' }}>
          {subtitle}
        </div>
      )}
      {children}
    </div>
  )
}

function Empty({ colors, children }) {
  return <div style={{ padding: 14, textAlign: 'center', color: colors.textMuted, fontSize: 12 }}>{children}</div>
}

const styles = {
  card: (c) => ({
    padding: '10px 12px',
    background: c.panel,
    border: `1px solid ${c.border}`,
    borderRadius: 6,
  }),
  btn: (c) => ({
    padding: '4px 12px', fontSize: 12, fontWeight: 600,
    border: `1px solid ${c.border}`, background: c.panel, color: c.text,
    borderRadius: 4, cursor: 'pointer',
  }),
  bigBtn: (c) => ({
    padding: '14px 16px',
    background: c.panel,
    border: `1px solid ${c.border}`,
    borderRadius: 6,
    textAlign: 'left',
    minWidth: 220, maxWidth: 300,
    cursor: 'pointer',
    color: c.text,
  }),
}
